import { Trans, useTranslation } from 'react-i18next'
import { useDropzone, DropzoneOptions } from 'react-dropzone'
import {
  Box,
  Text,
  Stack,
  List,
  ButtonIcon,
  Inline,
  IconUpload,
  IconDocument,
  IconClose
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { ReactNode, useState } from 'react'
import { DataAttributeMap } from '@machobear/design-system/dist/lakshmi.chunk'

export const tones = ['neutral', 'critical', 'positive', 'caution'] as const
type FieldTone = (typeof tones)[number]
export interface FieldMessageProps {
  id: string
  message: ReactNode
  reserveMessageSpace?: boolean
  tone?: FieldTone
  secondaryMessage?: ReactNode
  disabled?: boolean
  data?: DataAttributeMap
}

type DropzoneFieldProps = {
  id: string
  label?: string
  message?: ReactNode
  tone?: FieldMessageProps['tone']
  required?: boolean
  files: File[]
  maxFiles?: number
  onChange: (value: File[]) => void
  onRemoveFile: (file: File) => void
}

export type DropzoneProps = DropzoneOptions & DropzoneFieldProps
// export type DropzoneProps = DropzoneOptions &
//   DropzoneFieldProps & {
//     remove: () => void
//   }

type ErrorListType = {
  fileName: string
  errors: { code: string; message: string }[]
}

const defaultAcceptedFileTypes = {
  'image/jpeg': [],
  'image/png': [],
  'image/gif': [],
  'application/pdf': [],
  'video/mp4': [],
  'text/csv': []
}

export const Dropzone = (props: DropzoneProps) => {
  const { t } = useTranslation()
  const {
    disabled,
    id,
    label,
    message: errorMessage,
    onDrop,
    accept = defaultAcceptedFileTypes,
    required,
    files = [],
    onChange,
    onRemoveFile,
    maxSize = 10000000,
    maxFiles = 1,
    ...restProps
  } = props

  const { tone = 'neutral' } = restProps

  const [errorData, setErrorData] = useState<ErrorListType[]>([])

  const onHandleDrop = (acceptedFiles: File[]) => {
    onChange(acceptedFiles)
    setErrorData([])
  }

  const acceptableFilesArray = Object.keys(accept || defaultAcceptedFileTypes).map(type =>
    type.split('/').pop()
  )

  const fileTypePlaceholder = acceptableFilesArray
    .map(extension => extension?.toUpperCase())
    .join(', ')

  const generateInvalidTypeErrorMsg = () => {
    const fileTypes = acceptableFilesArray

    if (fileTypes.length === 1) {
      return `File type must be .${fileTypes}`
    }

    const fileTypesWithDot = fileTypes.map(type => `.${type}`)

    const allButLast = fileTypesWithDot.slice(0, -1).join(', ')

    const last = fileTypesWithDot.slice(-1)

    if (fileTypes.length === 2) {
      return `File type must be ${allButLast} or ${last}`
    }

    return `File type must be ${allButLast}, or ${last}`
  }

  const handleOnError = (
    filesWithError: { file: { name: string }; errors: { code: string; message: string }[] }[]
  ) => {
    const errorList = filesWithError.map(fileWithError => ({
      fileName: fileWithError.file.name,
      errors: fileWithError.errors
    }))
    setErrorData(errorList)
  }

  const { getRootProps, getInputProps, isFocused } = useDropzone({
    accept,
    onDrop: onHandleDrop,
    maxSize,
    maxFiles,
    multiple: maxFiles > 0,
    onDropRejected: handleOnError,
    disabled
  })

  const AcceptedFilesList = () =>
    files?.map((file: File) => {
      return (
        <Box display="flex" key={file.name} alignItems="center" justifyContent="spaceBetween">
          <Inline space="xxsmall" align="left" alignY="center">
            <IconDocument size="xsmall" />
            <Text size="small">{file.name}</Text>
          </Inline>
          <ButtonIcon
            variant="transparent"
            icon={<IconClose />}
            id="remove-file"
            label="remove file"
            onClick={() => onRemoveFile(file)}
            bleed={false}
          />
        </Box>
      )
    })

  const errorMsgList: { [key: string]: { single: string; multiple: string } } = {
    'file-too-large': {
      single: 'File size must be less than 10MB',
      multiple: `is too large. Try a file size less than ${maxSize / 1000000} MB`
    },
    'file-invalid-type': {
      single: generateInvalidTypeErrorMsg(),
      multiple: `is not supported. ${generateInvalidTypeErrorMsg()}`
    },
    'too-many-files': {
      single: 'Too many files',
      multiple: 'Too many files'
    }
  }

  const ErrorMessage = () => {
    const isSingleFileError = errorData.length === 1
    const isMultipleFileErrors = errorData.length > 1
    return (
      <Stack space="xsmall" align="left">
        <Box>
          <Text size="small" tone={errorMessage ? 'critical' : tone}>
            {errorMessage && !disabled ? errorMessage : '\u00A0'}
          </Text>
        </Box>
        {isMultipleFileErrors && (
          <Text size="small" tone="critical">
            The following couldn’t be uploaded:
          </Text>
        )}
        <Stack space="xsmall" align="left">
          {errorData.map(({ fileName, errors }) => {
            const uploadedMultipleFiles = files.length > 0

            if (isSingleFileError) {
              return (
                <Stack key={fileName} space="xsmall" align="left">
                  {errors.map(({ code }, i) => (
                    <Text size="small" tone="critical" key={`${i}-${code}`}>
                      {uploadedMultipleFiles
                        ? `"${fileName}" ${errorMsgList[code].multiple}`
                        : errorMsgList[code].single}
                    </Text>
                  ))}
                </Stack>
              )
            }

            if (isMultipleFileErrors) {
              return (
                <List key={fileName} size="small" tone="critical" space={'xsmall'}>
                  {errors.map(({ code }) => (
                    <Text key={code} size="small" tone="critical">
                      {`"${fileName}" ${errorMsgList[code].multiple}`}
                    </Text>
                  ))}
                </List>
              )
            }
          })}
        </Stack>
      </Stack>
    )
  }

  const background = 'neutralLight'

  const dropzoneWrapper = {
    default: {
      border: isFocused
        ? `1px solid ${vars.borderColor.focus}`
        : `1px dashed ${vars.borderColor.field}`,
      outline: isFocused ? `2px solid ${vars.borderColor.focus}` : 'none'
    },
    disabled: { border: `1px dashed ${vars.borderColor.neutralLight}` },
    error: { border: `1px dashed ${vars.borderColor.critical}` }
  }

  return (
    <>
      <Box cursor={disabled ? 'not-allowed' : 'pointer'} width="full">
        <Box
          {...getRootProps({ className: 'dropzone' })}
          disabled={disabled}
          background={background}
          borderRadius="small"
          style={
            dropzoneWrapper[
              disabled
                ? 'disabled'
                : errorMessage || errorData.length > 0 || tone === 'critical'
                  ? 'error'
                  : 'default'
            ]
          }
        >
          <input {...getInputProps()} />
          <Box padding="gutter">
            <Stack space="small" align="center">
              <IconUpload tone={disabled ? 'disabled' : 'formAccent'} />
              <Stack space="xsmall" align="center">
                <Trans i18nKey="b.dropzone.edit.upload.label">
                  <Inline align="left" space="xxxsmall">
                    <Text tone={disabled ? 'disabled' : 'link'} size="small" weight="medium">
                      Click to upload
                    </Text>
                    <Inline space="none">
                      <Text
                        tone={disabled ? 'disabled' : 'formAccent'}
                        size="small"
                        weight="medium"
                      >
                        or drag and drop here
                      </Text>
                      {required && (
                        <Text
                          tone={disabled ? 'disabled' : 'critical'}
                          size="small"
                          weight="medium"
                        >
                          *
                        </Text>
                      )}
                    </Inline>
                  </Inline>
                </Trans>
                <Text size="xsmall" tone={disabled ? 'disabled' : 'tertiary'}>
                  {t('{{fileTypePlaceholder}} | Max size: {{maxSize}}MB', {
                    fileTypePlaceholder,
                    maxSize: maxSize / 1000000
                  })}
                </Text>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
      {errorMessage || errorData ? <ErrorMessage /> : null}
      {files.length > 0 && (
        <Box paddingTop="none" width="full">
          <Stack space="small" align="left">
            <AcceptedFilesList />
          </Stack>
        </Box>
      )}
    </>
  )
}
